import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import { colors, spacing, typography, radius, shadows } from '../../theme/tokens';

const HeaderContainer = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
  color: '#ffffff',
  padding: `${spacing.xl} ${spacing.md}`,
  marginBottom: spacing.lg,
  boxShadow: shadows.lg,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-10%',
    width: '40%',
    height: '200%',
    background: 'rgba(255, 255, 255, 0.05)',
    transform: 'rotate(15deg)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: `${spacing.sm} ${spacing.sm}`,
    marginBottom: spacing.sm,
  },
}));

const ContentWrapper = styled(Box)({
  maxWidth: '1200px',
  margin: '0 auto',
  position: 'relative',
  zIndex: 1,
});

const TopRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: spacing.md,
  gap: spacing.md,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const OrgInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.sm,
  padding: `${spacing.xs} ${spacing.md}`,
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  borderRadius: radius.full,
  backdropFilter: 'blur(10px)',
});

const OrgIcon = styled(Box)({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  backgroundColor: '#ffffff',
  color: colors.primary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    fontSize: '20px',
  },
});

const OrgName = styled(Typography)({
  fontSize: typography.fontSize.sm,
  fontWeight: typography.fontWeight.medium,
  color: '#ffffff',
  opacity: 0.95,
});

const DocumentBadge = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.xs,
  padding: `${spacing.xs} ${spacing.sm}`,
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  borderRadius: radius.button,
  '& svg': {
    fontSize: '18px',
    color: '#ffffff',
    opacity: 0.9,
  },
});

const DocumentType = styled(Typography)({
  fontSize: typography.fontSize.xs,
  fontWeight: typography.fontWeight.medium,
  color: '#ffffff',
  opacity: 0.9,
});

const MainTitle = styled(Typography)(({ theme }) => ({
  fontSize: typography.fontSize['4xl'],
  fontWeight: typography.fontWeight.bold,
  color: '#ffffff',
  marginBottom: spacing.xs,
  textAlign: 'center',
  lineHeight: 1.2,
  letterSpacing: '-0.5px',
  [theme.breakpoints.down('sm')]: {
    fontSize: typography.fontSize.xl,
    marginBottom: '4px',
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: typography.fontSize.lg,
  color: 'rgba(255, 255, 255, 0.9)',
  textAlign: 'center',
  lineHeight: 1.5,
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

/**
 * Modern header component with organization branding and context
 * @param {Object} props
 * @param {string} props.organizationName - Name of the organization
 * @param {string} props.documentType - Type of document (e.g., "טופס רישום")
 */
export default function ModernHeader({ organizationName, documentType }) {
  const { t } = useTranslation();

  return (
    <HeaderContainer>
      <ContentWrapper>
        <TopRow>
          {organizationName && (
            <OrgInfo>
              <OrgIcon>
                <SchoolIcon />
              </OrgIcon>
              <OrgName>{organizationName}</OrgName>
            </OrgInfo>
          )}

          {documentType && (
            <DocumentBadge>
              <DescriptionIcon />
              <DocumentType>{documentType}</DocumentType>
            </DocumentBadge>
          )}
        </TopRow>

        <MainTitle>{t('modernHeader.title', 'מילוי וחתימת מסמך דיגיטלי')}</MainTitle>

        <Subtitle>
          {t(
            'modernHeader.subtitle',
            'מלא את הפרטים הנדרשים בטופס, צרף מסמכים והגש - פשוט ומאובטח'
          )}
        </Subtitle>
      </ContentWrapper>
    </HeaderContainer>
  );
}
